import type { Activity } from '@/models/Activity';
import type { Course, CourseProgress } from '@/models/Course';
import type { Milestone } from '@/models/Milestone';
import { MilestoneLogCode } from '@/models/Milestone';

import fetch from '@/utils/fetch';

import {
  getCourseMetadataEndpoint,
  getCourseMilestonesEndpoint,
  getCourseProgressEndpoint,
  getCourseToggleFavoriteEndpoint,
} from './endpoints';

interface ActivityPosition {
  activityIndex: number;
  moduleIndex: number;
}

export const getCourseMetadata = async (courseId: number): Promise<Course> => {
  const response = await fetch(getCourseMetadataEndpoint(courseId));

  return await response.json();
};

export const getCourseProgress = async (courseId: number): Promise<CourseProgress[]> => {
  const response = await fetch(getCourseProgressEndpoint(courseId));

  return await response.json();
};

export const getCourseMilestones = async (courseId: number): Promise<Milestone[]> => {
  const response = await fetch(getCourseMilestonesEndpoint(courseId));

  return await response.json();
};

export const getActivityPlayList = (courseContent: Course): Activity[] => {
  let allActivities: Activity[] = [];

  if (courseContent.quickscan?.activities) {
    allActivities = [...allActivities, ...courseContent.quickscan.activities];
  }
  if (courseContent.modules) {
    courseContent.modules.forEach((module) => {
      allActivities = [...allActivities, ...module.activities];
    });
  }
  if (courseContent.certification?.activities) {
    allActivities = [...allActivities, ...courseContent.certification.activities];
  }

  return allActivities;
};

export const getCertificateLogs = (milestoneLogs: Milestone[]): Milestone[] => {
  return milestoneLogs.filter(({ logCode }) =>
    [MilestoneLogCode.participationCertificate, MilestoneLogCode.testCertificate].includes(logCode),
  );
};

export const checkIsFavorited = (milestoneLogs: Milestone[] | undefined): boolean => {
  if (!milestoneLogs || milestoneLogs.length === 0) return false;

  return (
    milestoneLogs.findIndex((milestone) => milestone.logCode === MilestoneLogCode.courseFavorited) >
    -1
  );
};

export const toggleFavorite = async (courseId: number): Promise<boolean> => {
  const response = await fetch(getCourseToggleFavoriteEndpoint(courseId), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  const data = await response.json();

  return data.status;
};

export const getActivityPosition = (
  courseContent: Course,
  courseCode: string,
): ActivityPosition => {
  let activityIndex = -1;
  let moduleIndex = -1;

  courseContent.modules.forEach((module) => {
    const foundActivity = module.activities.find(
      (activity) => activity.courseCode.toLowerCase() === courseCode.toLowerCase(),
    );
    if (foundActivity && activityIndex === -1) {
      activityIndex = module.activities.indexOf(foundActivity);
      moduleIndex = courseContent.modules.indexOf(module);
    }
  });

  return {
    activityIndex,
    moduleIndex,
  };
};
